import api from './api';
import config from '../config/config';
import { getAccessToken } from './authService';
import { authenticatedGet, authenticatedPost, authenticatedPut, authenticatedDelete } from '../utils/apiUtils';

const API_BASE_URL = `${config.apiUrl}/wills`;

interface CreateWillData {
  title: string;
  description: string;
  budget?: number;
  currency?: string;
  fulfillmentTime?: string;
  fulfillmentTimeType?: string;
  recurrencePattern?: string;
  category: string;
  location?: string;
}

export const createWill = async (data: CreateWillData) => {
  const token = getAccessToken();

  try {
    const response = await fetch(API_BASE_URL, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Failed to create will');
    }

    return await response.json();
  } catch (error) {
    console.error('Create will error:', error);
    throw error;
  }
};

export const createWillWithMedia = async (willData: any, mediaFiles: File[]): Promise<any> => {
  try {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      throw new Error('Authentication required');
    }

    // Create FormData for multipart request
    const formData = new FormData();
    
    // Add will data as JSON - change the part name from 'willData' to 'will'
    const willDataBlob = new Blob([JSON.stringify(willData)], {
      type: 'application/json',
    });
    formData.append('will', willDataBlob); // Changed from 'willData' to 'will'
    
    // Add media files
    if (mediaFiles && mediaFiles.length > 0) {
      mediaFiles.forEach((file, index) => {
        formData.append(`files`, file); // Changed from 'media' to 'files'
      });
    }
    
    const response = await fetch(`${import.meta.env.VITE_API_URL}/wills`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`API error: ${response.status} - ${errorData.message || 'Unknown error'}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating will with media:', error);
    throw error;
  }
};

interface UpdateWillRequest {
  title: string;
  description: string;
  budget?: number;
  currency?: string;
  fulfillmentTime?: string;
  fulfillmentTimeType?: string;
  recurrencePattern?: string;
  category: string;
  location?: string;
}

export const updateWill = async (willId: string, data: any) => {
  try {
    console.log('Updating will with data:', data);
    
    const token = getAccessToken();
    if (!token) {
      throw new Error('Authentication required');
    }

    // Create a new object with the correct structure for the backend
    const requestData = {
      ...data,
      // Explicitly include existingMediaIds even if it's empty
      existingMediaIds: data.existingMediaIds || []
    };

    console.log('Sending request data to backend:', requestData);

    const response = await fetch(`${API_BASE_URL}/${willId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error response from server:', errorText);
      throw new Error(`Failed to update will: ${response.status} ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Update will error:', error);
    throw error;
  }
};

export const updateWillWithMedia = async (willId: string, willData: any, mediaFiles: File[]): Promise<any> => {
  try {
    console.log('updateWillWithMedia called with method PUT');
    console.log('Updating will with media:', willData);
    console.log('Media files to upload:', mediaFiles);
    
    // Log details about each file
    mediaFiles.forEach((file, index) => {
      console.log(`File ${index + 1}:`, {
        name: file.name,
        type: file.type,
        size: file.size,
        lastModified: file.lastModified
      });
    });
    
    const token = getAccessToken();
    if (!token) {
      throw new Error('Authentication required');
    }
    
    // Create FormData for multipart request
    const formData = new FormData();
    
    // Add will data as JSON string (not Blob)
    const willDataJson = JSON.stringify({
      ...willData,
      existingMediaIds: willData.existingMediaIds || []
    });
    
    console.log('Will data JSON:', willDataJson);
    formData.append('will', willDataJson);
    
    // Add media files
    if (mediaFiles && mediaFiles.length > 0) {
      mediaFiles.forEach((file, index) => {
        console.log(`Appending file ${index + 1} to FormData:`, file.name);
        formData.append('files', file);
      });
    }
    
    // Log FormData entries (for debugging)
    for (const pair of formData.entries()) {
      console.log(`FormData entry - ${pair[0]}:`, pair[1]);
    }
    
    // Construct the URL carefully
    const baseUrl = import.meta.env.VITE_API_URL.endsWith('/') 
      ? import.meta.env.VITE_API_URL.slice(0, -1) 
      : import.meta.env.VITE_API_URL;
    const url = `${baseUrl}/wills/${willId}`;
    
    console.log('Making PUT request to URL:', url);
    
    // Make the request
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`
        // Don't set Content-Type for FormData
      },
      body: formData
    });
    
    console.log('Response status:', response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error response from server:', errorText);
      throw new Error(`Failed to update will with media: ${response.status} ${errorText}`);
    }
    
    const result = await response.json();
    console.log('Update successful, result:', result);
    return result;
  } catch (error) {
    console.error('Error updating will with media:', error);
    throw error;
  }
};

export const deleteWill = async (willId: string) => {
  try {
    const accessToken = getAccessToken();
    if (!accessToken) {
      throw new Error('No access token found');
    }

    const response = await fetch(`${API_BASE_URL}/${willId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      }
    });

    if (!response.ok) {
      throw new Error('Failed to delete will');
    }

    return true;
  } catch (error) {
    console.error('Error deleting will:', error);
    throw error;
  }
};

export const getUserWills = async (userId: string) => {
  try {
    const response = await authenticatedGet(`${import.meta.env.VITE_API_URL}/wills/user/${userId}`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch user wills');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching user wills:', error);
    throw error;
  }
};

export const getWillById = async (id: string) => {
  try {
    const response = await fetch(`${config.apiUrl}/wills/${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        // Add authorization header if needed
        ...(getAccessToken() ? { 'Authorization': `Bearer ${getAccessToken()}` } : {})
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch will');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching will:', error);
    throw error;
  }
};
